/**
 * A striped version of MediaDeviceInfo
 *
 * @beta
 */
export type MediaDeviceInfoLike = Omit<MediaDeviceInfo, 'toJSON'> & {
    toJSON?: MediaDeviceInfo['toJSON'];
};

export type MediaDeviceTrackInfo = MediaDeviceInfoLike & {
    id: string;
    settings: MediaTrackSettings;
};

export type IndexedDeviceMap = {
    [Key in MediaDeviceInfo['kind']]: {
        deviceId: Map<MediaDeviceInfoLike['deviceId'], MediaDeviceInfoLike>;
        label: Map<MediaDeviceInfoLike['label'], MediaDeviceInfoLike[]>;
        size: number;
    };
};

export interface IndexedDevices {
    /**
     * Get devices by kind and device id or label
     * When both `deviceId` and `label` are provided, it will use `deviceId` to
     * find the device, and when it's not found, it will use `label` to find
     *
     * When device is not provided, it returns all devices.
     *
     * @param device - the device to find
     * @returns found devices otherwise empty array
     */
    get(
        device?: Pick<
            Partial<MediaDeviceInfoLike>,
            'deviceId' | 'groupId' | 'label' | 'kind'
        >,
    ): MediaDeviceInfoLike[];
    /**
     * Size of particular kind of devices. When kind is not specified, it
     * returns the total size.
     */
    size(kind?: MediaDeviceKind): number;
    /**
     * First device of the kind
     */
    first(kind: MediaDeviceInfo['kind']): MediaDeviceInfoLike | undefined;
    toJSON(): Record<MediaDeviceKind, MediaDeviceInfoLike[]>;
    /**
     * `true` when there is any authorized device of the kind. When kind is not
     * provided, it checks for any audio or video input device.
     */
    anyAuthorizedDevice(kind?: MediaDeviceKind): boolean;
}

export type DeviceConstraint = MediaDeviceInfoLike | MediaDeviceInfoLike[];

export interface NormalizedConstraintDeviceParameters {
    exact?: MediaDeviceInfoLike[];
    ideal?: MediaDeviceInfoLike[];
}

export interface ConstraintDeviceParameters {
    exact?: DeviceConstraint;
    ideal?: DeviceConstraint;
}

export interface InputConstraintSet extends MediaTrackConstraints {
    /**
     * Same purpose as `deviceId` but it gives more information about the device
     * so that we can have extra tolerance on device selection
     */
    device?: DeviceConstraint | ConstraintDeviceParameters;
    /**
     * Whether or not using video segmentation, e.g. background
     * blur/replacement, to specify the effects, intended to be applied to the
     * segment. Available effects are `none`, `blur`, `overlay` or `remove`
     */
    videoSegmentation?: ConstrainDOMString;
    /**
     * Whether or not using our own noise suppression
     */
    denoise?: ConstrainBoolean;
    /**
     * Voice Activity Detection
     */
    vad?: ConstrainBoolean;
    /**
     * Audio Signal Detection for the purpose of checking if the audio input is
     * hardware muted or unusable
     */
    asd?: ConstrainBoolean;
    /**
     * Audio Signal Detection for the purpose of checking if the audio input is
     * hardware muted or unusable
     */
    mixWithAdditionalMedia?: ConstrainBoolean;
    /**
     * Blur size/level parameter when using video segmentation with `blur`
     * effects
     */
    backgroundBlurAmount?: ConstrainULong;
    /**
     * Blur amount applied to the segmented person's edge
     */
    edgeBlurAmount?: ConstrainULong;
    /**
     * Erode level for edge smoothing when using video segmentation
     */
    foregroundThreshold?: ConstrainDouble;
    /**
     * Image Url that is being used for video overlay effects
     */
    backgroundImageUrl?: ConstrainDOMString;
    /**
     * The ratio to be used for smoothing segmentation mask.
     * How much uncertainty enables temporal blend, [0,1].
     * Higher = more frame-to-frame blending, more stability but more trailing ghosts.
     * Typical: 0.6–0.85.
     */
    maskCombineRatio?: ConstrainDouble;
    /**
     * [0,1] Lower mask confidence: all mask pixels <= this are considered definite background.
     * Adjust to make mask edges tighter or more permissive for background inclusion.
     * Example: 0.3 ("mask < 0.3 = sure background").
     */
    backgroundThreshold?: ConstrainDouble;
    // ---------------- Joint Bilateral Filter (Mask Edge Snapping) ----------------
    /**
     * Bilateral filter: sigma in pixel space for mask edge snapping.
     * Controls the neighborhood/pixel distance—higher is larger window (5–10 typical).
     */
    sigmaSpace?: ConstrainDouble;
    /**
     * Bilateral filter: minimum sigma in color/luma (for mask interiors), [0,1].
     * Controls how little color difference is needed to cross-mask regions away from edge.
     * Lower = sharper edge preservation in mask interiors.
     * Example: 0.2
     */
    sigmaRangeLo?: ConstrainDouble;
    /**
     * Bilateral filter: maximum sigma in color/luma (for mask uncertain zones), [0,1].
     * Controls how much color similarity is required at uncertain/edge mask locations.
     * Higher = more mask smoothing at edges.
     * Example: 0.8
     */
    sigmaRangeHi?: ConstrainDouble;
    // ----------------- Instance Filtering / Bystander Removal -----------------
    /**
     * Whether to exclude bystanders/connected people from the output mask.
     * The main person instance (closest to personCenter OR largest blob) is kept in
     * the output mask.
     * All other bystanders/connected people are suppressed (bystander removal).
     */
    excludeBystanders?: ConstrainBoolean;
    /**
     * Person center: normalized in [0,1] image coordinates for main subject selection.
     * Used by the CCL/bias filter to select the main person blob.
     */
    personCenterX?: ConstrainDouble;
    /**
     * Person center: normalized in [0,1] image coordinates for main subject selection.
     * Used by the CCL/bias filter to select the main person blob.
     */
    personCenterY?: ConstrainDouble;
    /**
     * Morphological erosion: radius in pixels [1,12]. Sets kernel size for bridge-breaking.
     * Larger values break thicker bridges but may erode fine details.
     */
    morphErodeRadiusPx?: ConstrainULong;
    /**
     * Morphological pass: number of separable passes [1,10].
     * More passes increase the effective span.
     */
    morphPass?: ConstrainULong;
    /**
     * Geodesic dilation: radius in pixels [1,12]. Must match erosion for good reconstruction.
     */
    morphDilateRadiusPx?: ConstrainULong;
    // ----------------- Light Wrap Parameters -----------------
    /**
     * [0,1] Light wrap amount (halo intensity) at mask edge.
     * Wraps blurred background color just outside the subject edge onto the subject boundary for realism.
     */
    lightWrapIntensity?: ConstrainDouble;
    /**
     * [0.5,4.0] Controls power/tightness of the wrap band falloff. Higher = tighter/narrower wrap.
     */
    lightWrapTightness?: ConstrainDouble;
    /**
     * Width of the wrap band (feather size, in normalized mask units) between the backgroundThreshold
     * and foregroundThreshold in [0.0, (foregroundThreshold - backgroundThreshold) / 2]
     * Higher = wider soft halo.
     */
    lightWrapEdgeBand?: ConstrainDouble;
    /**
     * Blur amount used for background color in the wrap band (units=blur passes).
     */
    lightWrapBlurAmount?: ConstrainULong;
    // -------------- Performance / Quality Controls ----------------
    /**
     * Downsampling factor for mask/blurring, must be [2,4].
     * 2 = half resolution, 4 = quarter resolution for faster filtering.
     * Use for performance tuning vs. quality.
     */
    downSampleFactor?: ConstrainULong;
    /**
     * Image Url that is being used for video overlay effects
     */
    resizeMode?: ConstrainDOMString;
    /**
     * Current pan, tilt and zoom level for a PTZ controllable camera on web
     */
    pan?: boolean;
    tilt?: boolean;
    zoom?: boolean;

    /**
     * Content Hint for the track to apply
     */
    contentHint?: ConstrainDOMString;
}

export type InputDeviceConstraint =
    DeviceConstraint | InputConstraintSet | boolean;

/**
 * The request constraints that applies to `getMediaStream`
 *
 * @beta
 */
export interface MediaDeviceRequest {
    /**
     * Either a Boolean (which indicates whether or not an audio track is
     * requested) or a {@link MediaDeviceInfoLike} object providing a hint for the
     * audio track included in the returned MediaStream
     */
    audio?: InputDeviceConstraint;
    /**
     * Either a Boolean (which indicates whether or not an video track is
     * requested) or a {@link MediaDeviceInfoLike} object providing a hint for the
     * video track included in the returned MediaStream
     */
    video?: InputDeviceConstraint;
}

export interface DeviceIdConstraints {
    deviceId: ConstrainDOMString;
}

export interface MediaConstraintRequirement extends MediaDeviceRequest {
    defaultConstraints: MediaStreamConstraints;
}

/**
 * an enum of MediaDeviceKind which can be reused
 *
 * @beta
 */
export enum MediaDeviceKinds {
    /** audio input */
    AUDIOINPUT = 'audioinput',
    /** audio output */
    AUDIOOUTPUT = 'audiooutput',
    /** video input */
    VIDEOINPUT = 'videoinput',
}

/**
 * MediaTrackConstraints.facingMode
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackConstraints/facingMode}
 */
export const FACING_MODE = ['user', 'environment', 'left', 'right'] as const;
export type FacingMode = (typeof FACING_MODE)[number];

/**
 * An enum of failure events for media device
 *
 * @beta
 */
export enum MediaDeviceFailure {
    /**
     * some problem (not user or permissions) occurred which prevented the device
     * from being used.
     */
    AbortError = 'AbortError',
    /**
     * Custom error for when selected audio and video device is not found
     */
    AudioAndVideoDeviceNotFoundError = 'AudioAndVideoDeviceNotFoundError',
    /**
     * Custom error for selected audio device not found
     */
    AudioInputDeviceNotFoundError = 'AudioInputDeviceNotFoundError',
    /**
     * Custom missing constraints error
     */
    MissingConstraintsError = 'MissingConstraintsError',
    /**
     * user has blocked access
     */
    NotAllowedError = 'NotAllowedError',
    /**
     * the media tracks doesn't match the constraints
     */
    NotFoundError = 'NotFoundError',
    /**
     * hardware failure after user granted access
     */
    NotReadableError = 'NotReadableError',
    /**
     * no devices met the criteria for the constraints
     */
    OverconstrainedError = 'OverconstrainedError',
    /**
     * user has blocked access old chrome
     */
    PermissionDeniedError = 'PermissionDeniedError',
    /**
     * User media support disabled by agent
     */
    SecurityError = 'SecurityError',
    /**
     * when hardware in use old chrome win os
     */
    TrackStartError = 'TrackStartError',
    /**
     * GetUserMedia invalid constraints error
     */
    TypeError = 'TypeError',
    /**
     * Custom error for selected video device not found
     */
    VideoInputDeviceNotFoundError = 'VideoInputDeviceNotFoundError',
    /**
     * action or function is not supported by the current platform
     */
    NotSupportedError = 'NotSupportedError',
    /**
     * Canvas capture is not supported
     */
    StreamTrackNotFound = 'StreamTrackNotFound',
}

/**
 * Lesser strict type of `MediaStreamTrack`
 *
 * @beta
 */
export type MediaStreamTrackLike = Pick<
    MediaStreamTrack,
    'id' | 'kind' | 'label' | 'getSettings' | 'stop'
>;

/**
 * Union types of MediaDeviceInfoLike and MediaStreamTrackLike
 */
export type DeviceOrTrack = MediaDeviceInfoLike | MediaStreamTrackLike;

/**
 * Holds user media input devices
 *
 * @beta
 */
export interface MediaInput {
    audioInput?: MediaDeviceInfoLike;
    videoInput?: MediaDeviceInfoLike;
}

export type Unsubscribe = () => void;
export type TrackEventCallback = (track: MediaStreamTrack) => void;

/**
 * An object contains the event handlers
 */
export interface StreamTrackEventHandlers {
    ended?: TrackEventCallback;
    mute?: TrackEventCallback;
    unmute?: TrackEventCallback;
}

export interface InputDevicePermission {
    audio: PermissionState;
    video: PermissionState;
}

interface DisplayVideoTrackConstraint extends MediaTrackConstraintSet {
    displaySurface?: ConstrainDOMString;
    cursor?: ConstrainDOMString;
}
interface DisplayAudioTrackConstraint extends MediaTrackConstraintSet {
    restrictOwnAudio?: ConstrainBoolean;
    suppressLocalAudioPlayback?: ConstrainBoolean;
}

interface DisplayVideoTrackConstraints extends DisplayVideoTrackConstraint {
    advanced?: DisplayVideoTrackConstraint[];
}

interface DisplayAudioTrackConstraints extends DisplayAudioTrackConstraint {
    advanced?: DisplayAudioTrackConstraint[];
}

/**
 * The `DisplayMediaStreamOptions` dictionary is used to instruct the user agent
 * what sort of `MediaStreamTracks` may be included in the `MediaStream`
 * returned by `getDisplayMedia`.
 *
 * @see {@link https://w3c.github.io/mediacapture-screen-share/#displaymediastreamoptions}
 */
export interface DisplayMediaOptions extends DisplayMediaStreamOptions {
    audio?: boolean | DisplayAudioTrackConstraints;
    video?: boolean | DisplayVideoTrackConstraints;
    selfBrowserSurface?: ConstrainDOMString;
    systemAudio?: ConstrainDOMString;
    surfaceSwitching?: ConstrainDOMString;
    monitorTypeSurfaces?: ConstrainDOMString;
}
